/**
 * Everything that survives a reload.
 *
 * Imports nothing from the rest of the app on purpose: the JMAP transport
 * needs the token and the store needs the transport, so the token lives at the
 * bottom of the graph where neither creates a cycle. `lib/publicSession.ts` is
 * the one import, and it is not an exception to that: it imports nothing
 * itself, so it sits below this file rather than beside it.
 */

import { computed, effect, signal } from "@preact/signals";

import { credential } from "@transport";

import { isPublicSession } from "./publicSession";

/**
 * The one bearer token this origin still keeps, in `localStorage`.
 *
 * **It is not how a person signs in any more.** A password login leaves an
 * HttpOnly cookie that script cannot read at all, which is strictly better than
 * anything this file can offer, and `lib/auth.ts` owns it. This remains for the
 * case a cookie does not cover: a device token enrolled from the CLI, which is
 * still the documented way a script or another JMAP client reaches the daemon.
 *
 * This file used to argue that a cookie was not worth it -- that it would add
 * CSRF surface in exchange for XSS protection this app could not fully claim.
 * That was right while the only credential was one an operator pasted by hand.
 * It stopped being right when the credential became something a password mints
 * and a browser holds for a month: the CSRF half is closed by the daemon
 * refusing cookie-authenticated writes that are not `application/json`, and
 * what remains is a credential that injected script cannot read.
 *
 * The rest of that reasoning still stands and still matters, because this field
 * is still readable: no third-party origins, no remote fonts or analytics,
 * message bodies rendered in a sandboxed iframe that cannot reach this
 * document, and a CSP in the nginx config that blocks the rest.
 *
 * There used to be a second field here for the admin token, and a third for the
 * tenant slug it acted on. Both are gone. Managing your own mail accounts is a
 * tenant action on `/accounts` now, so nothing in this app needs a credential
 * that administers every tenant on the box -- and the strongest thing that can
 * be said about a credential is that it is not here. What is left of that
 * reasoning is the note above: a device token *is* readable by script, and the
 * mitigations are the ones listed, not the storage.
 */
/**
 * Not `persisted()`, unlike every other setting in this file.
 *
 * A preference can live in `localStorage` because losing it costs nothing and
 * reading it costs nothing. A credential is different on both counts, and the
 * desktop build keeps this one in the OS keychain — which is a round trip to
 * another process and therefore cannot be read during module initialisation the
 * way `persisted()` reads storage.
 *
 * So the value is a plain signal, {@link hydrateDeviceToken} fills it once at
 * startup, and every later change is written back through the transport. The
 * signal itself stays synchronous, because `authHeaders` in `lib/jmap.ts` reads
 * it on every request and must not become a promise.
 *
 * Going through the transport on *both* builds is deliberate. Leaving the
 * browser on `persisted()` would have meant the desktop build writing the token
 * to the keychain and to the webview's `localStorage`, which is the worse of the
 * two stores and exactly the one this moves away from.
 */
export const deviceToken = signal("");

/**
 * Does this string have the shape of a device token?
 *
 * `hm_<16 hex>_<64 hex>` -- the format `hm_control::token::parse` accepts, and
 * nothing else is worth sending. Checked here rather than trusted because the
 * value comes from a text field, and a text field is a place other things end
 * up: this locked somebody out of their own mail when a password manager
 * autofilled the "Device token" input on the About page, which is a
 * `type="password"` box a browser is entirely within its rights to fill. The
 * stored value then overrode the session cookie on every request -- see
 * {@link authHeaders} -- so the app logged in successfully and was refused by
 * everything afterwards.
 *
 * A value that cannot possibly authenticate must not be allowed to displace one
 * that can.
 */
export function looksLikeToken(value: string): boolean {
  return /^hm_[0-9a-f]{16}_[0-9a-f]{64}$/.test(value.trim());
}

/**
 * The credential decision, in one place because it was in none.
 *
 * It used to live in `lib/jmap.ts` and be used only there, which meant every
 * other module that talks to the daemon -- auth, accounts, contacts, priority
 * rules, push -- sent `content-type` and nothing else. In a browser that is
 * right: the session cookie is the credential and the browser attaches it. On a
 * build with no cookie it is a request with no credential at all, and the daemon
 * answers 401.
 *
 * That is not theoretical. On Android it presented as **signing in and being
 * logged straight back out**: `login()` sets `authState` to "in" and immediately
 * calls `refreshIdentity()`, whose `/auth/whoami` went out bare, came back 401,
 * and set `authState` to "out" -- while `/jmap/api` kept working, because that
 * one path is answered by Rust, which reads the token from the keychain itself.
 * Two credentials, one of them missing, and the working one hid the broken one.
 *
 * So it lives beside the token now, and every caller takes it from here.
 *
 * Synchronous, and that is load-bearing rather than convenient. `login()` sets
 * `deviceToken` and calls `refreshIdentity()` in the same tick; the write
 * through to the platform store is an `effect` and therefore later. Anything
 * reading the *stored* token instead of this signal would be reading it before
 * it had been written.
 */
export function authHeaders(token: string = deviceToken.value): HeadersInit {
  return { "content-type": "application/json", ...bearer(token) };
}

/**
 * The credential on its own, for the one request that cannot say JSON.
 *
 * `/jmap/upload` takes raw file bytes, and RFC 8620 §6.1 makes the client's
 * `Content-Type` the description of *those bytes* -- so the upload has to send
 * the file's type and cannot use {@link authHeaders}, which sends JSON. That is
 * a real difference and the reason this is split out rather than the upload
 * being made to fit: a helper that quietly overwrote the content-type would
 * turn a PNG into a JSON document as far as the daemon is concerned.
 */
export function bearer(token: string = deviceToken.value): Record<string, string> {
  return looksLikeToken(token) ? { authorization: `Bearer ${token.trim()}` } : {};
}

let hydrated = false;

/**
 * The last write to the platform store, or a resolved promise before there has
 * been one. Read through {@link deviceTokenWritten}.
 *
 * Not a signal, deliberately. It is a fact about one asynchronous call rather
 * than a piece of application state, and nothing renders it -- the sentence a
 * person reads is composed by the screen that asked, out of the reason this
 * carries.
 */
let writing: Promise<string | null> = Promise.resolve(null);

/**
 * Load the stored token before anything tries to authenticate with it.
 *
 * Resolves once, and is safe to call more than once. Awaited by `main.tsx`
 * before the app renders: a request that goes out ahead of this would carry no
 * credential and be answered 401, which the client correctly interprets as
 * being signed out — so the race would present as a login screen shown to
 * somebody who is already signed in.
 *
 * A failure here is not "no token". The store being unavailable — a locked
 * keychain, a session with no Secret Service — is reported rather than
 * flattened, because flattening it signs the person out with no explanation on
 * every launch. The token stays empty and the app behaves as it does for
 * anybody signed out, but the reason is on the console rather than nowhere.
 */
export async function hydrateDeviceToken(): Promise<void> {
  if (hydrated) return;
  hydrated = true;
  try {
    // A public session does not adopt a token it finds lying about. This is the
    // read gate that has teeth: on a shared machine the stored token belongs to
    // whoever used the browser last, `authHeaders` would attach it to every
    // request, and the daemon prefers a bearer over the cookie -- so a public
    // sign-in would succeed and then be answered 401 by everything, wearing the
    // previous person's credential. The transport clears the key on the first
    // save either way; this stops it being *used* in the window before that.
    deviceToken.value = isPublicSession() ? "" : await credential.load();
  } catch (e) {
    console.error("could not read the stored device token", e);
  }
  // After the load, not at module scope. This used to run on import, back when
  // the value was in `localStorage` and was therefore already present by then.
  // It now has nothing to inspect until hydration has happened, and calling it
  // early would silently do nothing -- which is the failure mode that let a
  // password-manager-filled value lock somebody out in the first place.
  forgetTokenThatIsNotOne();
  // Subscribed only after the first value is in, so hydration does not
  // immediately write back what it just read.
  effect(() => {
    const token = deviceToken.value;
    // Kept rather than voided. `void credential.save(...)` is what this line
    // used to be, and on a Mac launched outside its Aqua session the keychain
    // refused the write, the promise rejected with nobody listening, and the
    // only trace of it anywhere was an unhandled rejection in the shell log --
    // while the person watched the login screen come back and stayed there.
    // See {@link deviceTokenWritten}.
    writing = credential.save(token).then(
      () => null,
      (e: unknown) => reasonFor(e),
    );
  });
}

/**
 * How the write-back started by the last change to {@link deviceToken} went.
 *
 * **Resolves, always** -- with `null` when the platform store took the value and
 * with the store's own words when it would not. That is the whole of why this
 * exists: the write is started by an `effect` that nobody is in a position to
 * await, so a promise that rejected would be an unhandled rejection every time,
 * on a code path a person reaches by signing out or by pressing Sign in. A
 * failure that has to be *asked* for cannot go unhandled.
 *
 * Only `lib/auth.ts` asks, and only on the door where the token is the whole
 * credential -- see the sign-in path there for why a store that refused is a
 * sign-in that has to be reported rather than completed. Everywhere else the
 * write stays what it was: started, and not waited for.
 */
export function deviceTokenWritten(): Promise<string | null> {
  return writing;
}

/**
 * Whatever the platform store rejected with, as words.
 *
 * A Tauri command that fails rejects with the `Err` *string* it returned and not
 * with an `Error` -- `credential.rs` builds "could not store the token: ..." and
 * that string is what arrives here. So this cannot be `e.message`: on the one
 * build where this path is reachable, `e` is not an `Error` at all, and reading
 * `.message` off it would produce `undefined` and throw away the only clue there
 * is on a real machine.
 */
function reasonFor(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * Throw away what the previous version of this app stored.
 *
 * A browser that ran the old build still has an admin token in `localStorage`
 * under this origin, and it stays there forever -- removing the input does not
 * remove the value, and the value is the part that matters. Deleting the key on
 * load is the only thing that actually shrinks what an XSS on this page could
 * carry away. Cheap, idempotent, and safe to delete from this file once no
 * deployed browser has run the old build.
 */
function forgetRetiredKeys(): void {
  for (const key of ["hm.admin", "hm.tenant"]) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Private-browsing modes throw on access. Nothing to do about it.
    }
  }
}

/**
 * Delete a stored "device token" that cannot be one.
 *
 * A password manager filled the token field on the About page -- it was a
 * `type="password"` box, so this was entirely within the browser's rights --
 * and the value was persisted here. That locked the account out, because the
 * daemon prefers a bearer over the session cookie, and it put a **password**
 * into `localStorage` in plaintext.
 *
 * The field is no longer a password box and a malformed value is no longer
 * sent, so neither can happen again. Neither of those removes what is already
 * stored, and the stored value is the part that matters -- the same argument
 * `forgetRetiredKeys` above makes, for the same reason. A browser that hit this
 * is carrying a credential in a place designed for preferences, and it will
 * carry it until something deletes it.
 *
 * Only values that could never work are removed. A real token is left alone
 * whatever else is true, because it is somebody's deliberate configuration.
 */
function forgetTokenThatIsNotOne(): void {
  const stored = deviceToken.peek();
  if (stored && !/^hm_[0-9a-f]{16}_[0-9a-f]{64}$/.test(stored.trim())) {
    deviceToken.value = "";
  }
}

forgetRetiredKeys();

export type Theme = "system" | "light" | "dark";
export const theme = persisted<Theme>("hm.theme.pref", "system");

export const density = persisted<"comfortable" | "compact">("hm.density", "comfortable");

/**
 * How large the whole interface is drawn, as a multiplier.
 *
 * Separate from {@link density}, which only tightens row padding: this scales
 * type, icons, targets and spacing together, because the complaint it answers
 * is that everything is small rather than that the rows are far apart.
 *
 * It exists because a device pixel is not a fixed size and a CSS pixel is only
 * nominally 1/160 inch. The Fold 5's inner display is 1812px across a panel of
 * about 373 real dpi and Android buckets it at 420, so the page lays out 690
 * CSS pixels over 4.85 inches -- **142 to the inch against the nominal 160**.
 * Every measurement in the stylesheet therefore comes out around 11% smaller in
 * the hand than the same number does on a phone that lands on its bucket, which
 * is not a thing any breakpoint can see.
 *
 * # Pinch is off, and the setting is why
 *
 * `user-scalable=no` with the bounds pinned to the chosen scale. Disabling
 * zoom is an accessibility mistake on a *page*, where it is the only way to
 * make small text readable -- and it would be that here too if this control did
 * not exist. It does: this is the app's own zoom, it survives a restart, and it
 * moves the breakpoints with the text instead of leaving a magnified column
 * clipped at the edge of the screen, which is what a pinch does to a layout
 * like this one. What pinch mostly achieves in a mail client is a half-zoomed
 * list after a two-finger scroll.
 *
 * It applies to the whole frame tree, which is the one thing to know before
 * changing it: page zoom is not per-iframe, so this switches pinch off inside
 * the PDF viewer as well. That viewer has its own zoom controls for exactly
 * this reason -- see `ui/AttachmentView.tsx`.
 *
 * Applied through the viewport meta rather than a `zoom` or a root font size.
 * A root `zoom` leaves the media queries arguing with the layout -- the rules
 * would still be answering for 690px while the content had 616 -- and a root
 * font size moves nothing, because this stylesheet is px throughout (89 px
 * font sizes against 3 rem). Changing the layout viewport moves the breakpoints
 * with the content, so the app stays in one coherent pixel space and
 * `lib/panes.ts` keeps working in the same units it always has.
 */
export const uiScale = persisted<number>("hm.ui-scale", 1);

/**
 * The offered sizes.
 *
 * Coarse steps rather than a slider: this is a thing somebody sets once, and
 * the difference between 105% and 110% is not a judgement anyone can make
 * usefully. 110 is first among the larger ones because it is roughly the
 * correction for the bucketing described above.
 */
export const UI_SCALES: readonly (readonly [number, string])[] = [
  [1, "Normal"],
  [1.1, "Large"],
  [1.25, "Larger"],
  [1.5, "Largest"],
];

/** Show the reading pane beside the list, or replace the list with it. */
export const readingPane = persisted<"side" | "off">("hm.pane", "side");

/**
 * The width of the two fixed columns, in CSS pixels, where somebody has dragged
 * them.
 *
 * `null` means nobody has, and it is not the same as storing the number the
 * layout happens to use today: the stylesheet's own widths change with the
 * window -- narrower on a laptop, wider past 75rem -- and a value written here
 * would freeze whichever one was on screen the first time the app read it. So
 * the absence is stored, and `lib/panes.ts` decides what a column with no
 * preference is worth on the window it is being drawn on.
 *
 * Raw and unclamped on purpose. The bounds belong to the window, not to the
 * preference: a width chosen on a large monitor is narrowed to fit a laptop for
 * as long as the app is on the laptop, and writing that back would let the
 * smaller screen quietly overwrite a choice made on the bigger one. `panes.ts`
 * clamps on the way out, and writes only when a gesture actually moved a
 * column.
 */
export const sidebarWidth = persisted<number | null>("hm.w.sidebar", null);
export const listWidth = persisted<number | null>("hm.w.list", null);

/**
 * How long an open message must stay open before it counts as read.
 *
 * In seconds because that is the unit anyone setting it is thinking in, and
 * fractional because the useful range is short: the default is 1.5, and the
 * difference between 1 and 2 is the difference between "I skimmed past it" and
 * "I read it".
 *
 * Zero is allowed and means immediately. It is not the default, and the reason
 * is `store.step`: `j` and `k` *open* each row as they move over it, so at zero,
 * walking down a list of forty unread messages marks all forty read without any
 * of them being looked at. That is worth being able to choose and wrong to
 * assume.
 *
 * `null` means never — the keyword is only ever set by an explicit action. Kept
 * as a separate value rather than an enormous number so the setting reads as
 * what it is, and so nothing has to decide whether 99999 was a preference or a
 * typo.
 */
export const autoReadSeconds = persisted<number | null>("hm.auto-read-secs", 1.5);

/** The upper end of the slider, and of what is worth storing. */
export const AUTO_READ_MAX = 30;

/**
 * How long a sent message waits in the outbox before it may actually go.
 *
 * Stored here, applied there. This is *not* a client-side timer: `lib/compose.ts`
 * turns it into the `sendAt` on the submission itself, and the daemon is what
 * holds the message back. That distinction is the whole reason this comment is
 * long -- a number in this file that the server ignored would put an Undo
 * button over a message that had already gone, which is a worse failure than
 * having no undo at all.
 *
 * `null` is the default and means "whatever this deployment does". The hold is
 * an operator setting (`send_hold_secs`, ten seconds unless changed), and it is
 * not published to clients, so `null` is the only value that can honestly claim
 * to match it: it sends no `sendAt` and lets the daemon apply its own. Anything
 * else here overrides that per message.
 *
 * Zero is a real choice and not a disabled state -- it means send at the first
 * opportunity, with no undo offered, which is what this client did before any
 * of this existed. It is kept distinct from `null` for the same reason
 * {@link autoReadSeconds} keeps zero distinct from never: they are opposite
 * answers and a control that conflated them would be unpredictable.
 */
export const undoSendSeconds = persisted<number | null>("hm.undo-send-secs", null);

/**
 * How a *new* message opens: plain text, or the rich-text editor.
 *
 * Only new messages. A reply or a forward follows the original -- an HTML
 * message is quoted as HTML, a plain one as plain -- and a resumed draft comes
 * back in the format it was saved in. Either can be switched in the composer.
 * Plain is the default because it is what this client always wrote and the
 * format that cannot surprise a recipient.
 */
export type ComposeFormat = "plain" | "rich";
export const composeFormat = persisted<ComposeFormat>("hm.compose-format", "plain");

/**
 * The longest undo window this client will offer.
 *
 * Not a server limit -- the daemon's ceiling on a client-named `sendAt` is
 * `maxDelayedSend`, a month, because the same field carries scheduled send. It
 * is a limit on what is defensible as an *undo*: past about half a minute
 * nobody is still standing over the message, and a send that has not happened
 * five minutes later has stopped being a send at all.
 */
export const UNDO_SEND_MAX = 30;

/**
 * Accounts whose folder list is folded away in the sidebar.
 *
 * Collapsed rather than expanded is the state worth storing, so an account
 * added later starts open — a list that silently hides a mailbox somebody has
 * just connected is the one failure this feature could introduce.
 *
 * An array rather than a `Set` because `JSON.stringify` renders a `Set` as
 * `{}`, which persists as "nothing is collapsed" and looks like the setting is
 * simply not being saved.
 *
 * Keyed by account id, which is unique within a tenant and not across them. Two
 * people signing in to different tenants from one browser profile therefore
 * share these keys. It is a display preference and the worst case is a folder
 * group that starts folded, so it is not worth a scheme that would have to be
 * migrated later; noted because "why is my sidebar collapsed" deserves an
 * answer that exists somewhere.
 */
const collapsedAccounts = persisted<string[]>("hm.collapsed-accounts", []);

export function accountCollapsed(id: string): boolean {
  return collapsedAccounts.value.includes(id);
}

export function toggleAccountCollapsed(id: string): void {
  const now = collapsedAccounts.value;
  collapsedAccounts.value = now.includes(id) ? now.filter((x) => x !== id) : [...now, id];
}

/**
 * Folder groups folded away in the sidebar, keyed `accountId:mailboxId`.
 *
 * The same shape and most of the same reasoning as `collapsedAccounts` above:
 * collapsed is the state worth storing, so a folder that grows a child later
 * shows it rather than hiding it, and an array because a `Set` persists as
 * `{}`.
 *
 * The key has to carry the account, which the account one did not need. A JMAP
 * id is unique within an account and not across them, so two providers that
 * both number their mailboxes from 1 would otherwise fold each other's
 * folders.
 *
 * These ids churn in a way account ids do not -- re-importing a Maildir mints
 * new mailbox ids and the old ones stay here forever. That is accepted rather
 * than solved: the array only grows when somebody folds something by hand, a
 * stale entry matches nothing and costs a string, and the alternative is
 * forgetting every fold on reload, which is the failure people actually
 * notice.
 */
const collapsedMailboxes = persisted<string[]>("hm.collapsed-mailboxes", []);

export function mailboxCollapsed(accountId: string, id: string): boolean {
  return collapsedMailboxes.value.includes(`${accountId}:${id}`);
}

export function toggleMailboxCollapsed(accountId: string, id: string): void {
  const key = `${accountId}:${id}`;
  const now = collapsedMailboxes.value;
  collapsedMailboxes.value = now.includes(key) ? now.filter((x) => x !== key) : [...now, key];
}

/**
 * Load images the message points at somewhere else.
 *
 * Off by default because a remote image in mail is a read receipt: fetching it
 * tells the sender the address is live and roughly when it was opened. The
 * reader offers a per-message override, which is the only granularity that
 * respects both the tracking problem and the newsletter that is unreadable
 * without it.
 */
export const loadRemoteImages = persisted("hm.remote-images", false);

/**
 * What a double-click on a message row does: a tab, or a window of its own.
 *
 * A preference rather than a guess, because the two are the same feature to the
 * app and a different working habit to a person. Somebody on one screen wants a
 * tab; somebody with the mail on the left and a message on the right wants a
 * window, and neither is a default the other would tolerate for long.
 *
 * `"tab"` is the default because it is the one that cannot go wrong: a tab
 * appears where the person is already looking, and a window on a tiling
 * compositor may appear somewhere they are not. Whichever it is, the new view
 * takes focus — a double-click is a request to read that message, not to put it
 * somewhere for later.
 *
 * Read only by the desktop build. In a browser there is neither a tab strip nor
 * a second window, and the setting is not offered rather than being offered and
 * ignored.
 */
export const openInNewWindow = persisted("hm.dblclick-window", false);

/**
 * Give the window back to the system titlebar.
 *
 * Off by default, because where the app can draw its own it should: the OS bar
 * is a band of nothing above a header row the app already has, and folding the
 * window controls into that row costs no vertical space at all.
 *
 * The setting exists for the platforms where that is not true. Not every
 * platform will support custom chrome -- macOS wants the real traffic lights
 * over a transparent bar rather than buttons of ours, and Windows loses its
 * snap-layouts flyout when the frame goes -- so the app needs a way to say so,
 * and somebody needs a way to disagree with it.
 *
 * Read and applied by `lib/chrome.ts`, which also decides whether to *offer*
 * it: where the platform has no custom chrome the control is absent rather than
 * disabled, the same rule the offline pane and the double-click setting follow.
 */
export const systemTitlebar = persisted("hm.system-titlebar", false);

/**
 * What dragging a row sideways does.
 *
 * `"none"` on both sides, deliberately, and it is the one default here that is
 * an argument rather than a convenience. Every other setting on this page
 * changes how something looks; these two decide what happens to mail when a
 * thumb moves half an inch. A swipe is the easiest gesture on a phone to make
 * by accident -- it is also how the list is scrolled, and how the platform's
 * own back gesture starts -- so a client that ships with "swipe left archives"
 * turned on has decided on somebody's behalf that mail may leave the inbox
 * without anything being pressed.
 *
 * So the gesture exists and does nothing until it is asked to. Someone who
 * wants it binds it; someone who has never opened this page cannot lose a
 * message to a mis-swipe, which is the failure that would matter.
 */
export type SwipeAction = "none" | "read" | "archive" | "reply" | "flag";

/**
 * The choices, in the order they are offered.
 *
 * `"none"` leads because it is the default and because it is the way *out*:
 * somebody who has decided the gesture is more trouble than it is worth should
 * meet that answer first rather than hunting for it past four alternatives.
 *
 * The labels are what a swipe *would* do rather than what it did -- the two
 * toggles read "Read or unread" and "Flag or unflag" here, because a binding is
 * chosen once for every message and cannot be phrased for the state of one.
 * `lib/swipe.ts` names the concrete act on the row itself, where the state is
 * known.
 */
export const SWIPE_ACTIONS: [SwipeAction, string][] = [
  ["none", "Nothing"],
  ["read", "Read or unread"],
  ["archive", "Archive"],
  ["reply", "Reply"],
  ["flag", "Flag or unflag"],
];

/** Dragging a row towards the left of the screen. */
export const swipeLeft = persisted<SwipeAction>("hm.swipe.left", "none");

/** Dragging a row towards the right of the screen. */
export const swipeRight = persisted<SwipeAction>("hm.swipe.right", "none");

/**
 * Whether this device can produce the gesture at all.
 *
 * Asked of the hardware rather than of the build, because "mobile" is not a
 * platform here: the same `src/` runs in a phone browser, in the Android shell,
 * and on a laptop whose screen happens to be a touchscreen, and all three can
 * swipe. A desktop with a mouse and nothing else cannot, and there the two
 * settings are absent rather than drawn and inert -- the rule the offline pane,
 * the titlebar toggle and the double-click setting all follow.
 *
 * `maxTouchPoints` rather than a `(pointer: coarse)` query because it asks the
 * right question. `pointer: coarse` describes the *primary* pointer, so a
 * laptop with a trackpad and a touchscreen answers "fine" and would have hidden
 * a control that works there. A touchscreen that reports no touch points does
 * not exist; a touchscreen that is not the primary pointer is ordinary.
 */
export function touchCapable(): boolean {
  return navigator.maxTouchPoints > 0;
}

const darkQuery = window.matchMedia("(prefers-color-scheme: dark)");
const systemDark = signal(darkQuery.matches);
darkQuery.addEventListener("change", (e) => (systemDark.value = e.matches));

/**
 * Whether the page is currently dark, however that was decided.
 *
 * The message iframe needs the answer as a value, not as a media query: it is
 * a separate document, and the stylesheet it gets is generated rather than
 * linked, so it cannot inherit the decision the way the rest of the app does.
 */
export const isDark = computed(
  () => theme.value === "dark" || (theme.value === "system" && systemDark.value),
);

/**
 * A signal that is also a stored preference -- except on a shared machine.
 *
 * A public session gets the fallback and keeps its changes in memory: the
 * signal works exactly as it always did for as long as the tab is open, and the
 * storage is never touched in either direction. See `lib/publicSession.ts` for
 * why the read is gated as well as the write.
 */
function persisted<T>(key: string, fallback: T) {
  let initial = fallback;
  if (!isPublicSession()) {
    try {
      const raw = localStorage.getItem(key);
      if (raw !== null) initial = JSON.parse(raw) as T;
    } catch {
      // Private-browsing modes throw on access rather than returning null. An
      // app that forgets its settings still works; one that fails to start does
      // not.
    }
  }
  const s = signal<T>(initial);
  effect(() => {
    // Read before the gate, always. The read is what subscribes this effect to
    // the signal, and a `return` above it would leave the effect subscribed to
    // nothing at all -- so the first setting changed during a public session
    // would switch persistence off for that key permanently, including after
    // the mode ended. A gate that silently outlives what it gates is worse than
    // no gate.
    const value = s.value;
    if (isPublicSession()) return;
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* as above */
    }
  });
  return s;
}

/**
 * Apply the theme to the document.
 *
 * `data-theme` is only set for an explicit choice. Leaving it off under
 * "system" lets the CSS `prefers-color-scheme` query answer, which means the
 * page follows the OS switching at dusk without anything listening for it.
 */
export function watchTheme(): void {
  effect(() => {
    const value = theme.value;
    if (value === "system") delete document.documentElement.dataset["theme"];
    else document.documentElement.dataset["theme"] = value;
    // Kept in step so the browser's own form controls and scrollbars match.
    document.documentElement.style.colorScheme = value === "system" ? "light dark" : value;
    // The second copy, for the script that runs before this bundle does. Gated
    // like every other write, which costs a public session the un-flashed first
    // paint on a *reload* and nothing else -- the effect above has already
    // applied the theme to this document.
    if (!isPublicSession()) localStorage.setItem("hm.theme", value === "system" ? "" : value);
  });
  effect(() => {
    // The phone's own chrome, which `viewport-fit=cover` has made part of the
    // app's rectangle. Both meta tags are written with the same colour, which
    // is what overrides the media queries on them in `index.html`: those carry
    // the system default, and this carries a choice that may contradict it.
    const colour = isDark.value ? "#0e1014" : "#f5f6f8";
    for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
      meta.setAttribute("content", colour);
    }
  });
  effect(() => {
    document.documentElement.dataset["density"] = density.value;
  });
  effect(() => {
    applyScale(uiScale.value);
  });
}

/**
 * Resize the layout viewport, and tell the stylesheet by how much.
 *
 * `initial-scale` with **no `width`**, which is the part worth getting right:
 * given one and not the other the browser derives the layout width as
 * `device-width / initial-scale`, so the arithmetic is the engine's and this
 * never has to measure the screen. Naming both would be worse than redundant --
 * `width=device-width` wins the conflict and the scale would silently do
 * nothing -- and measuring `screen.width` to compute a width myself risks a
 * runaway on any engine that reports it in layout pixels: each application
 * would shrink the number that produced it.
 *
 * The custom property is for the insets. `MainActivity` measures those in dp
 * and cannot know about this, so with the viewport scaled they are no longer
 * CSS pixels; `styles.css` divides by this to put them back. It is declared in
 * `:root` as well, because a `calc()` naming a custom property that resolves to
 * nothing is thrown away whole, and losing that declaration would put the app
 * back under the status bar.
 */
/**
 * The viewport meta for a given UI size.
 *
 * Split out because it is the one part of {@link applyScale} that can be
 * checked: the rest is a DOM write, and these tests run in node. The bounds are
 * pinned to the scale rather than left open, which is what actually switches
 * pinch off -- `user-scalable=no` alone is advisory enough that engines have
 * been known to ignore it.
 */
export function viewportContent(scale: number): string {
  return (
    `initial-scale=${scale}, minimum-scale=${scale}, maximum-scale=${scale}, ` +
    `user-scalable=no, viewport-fit=cover`
  );
}

function applyScale(scale: number): void {
  document.documentElement.style.setProperty("--hm-ui-scale", String(scale));
  document.querySelector('meta[name="viewport"]')?.setAttribute("content", viewportContent(scale));
}
